import React, { useContext, useState, useEffect } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import { IEditPortionContext, EditPortionContext } from '../../editPortionState/editPortionContext';
import { preventDefaultForEnterKey } from 'utils/helpers';
import { convertUndefinedFields } from 'utils/helpers';
import { IUpdatePortion } from 'types';
import { updatePortion } from 'backend/dishes';
import { Form } from 'antd';
import Text from 'components/Text/Text';
import Button from 'components/Button/Button';
import { NamePortionInput } from './components/NamePortionInput';
import { PosNameInput } from './components/PosNameInput';
import { ArticleNumberInput } from './components/ArticleNumberInput';
import { CustomerSelect } from './components/CustomerSelect';
import { RestaurantSelect } from './components/RestaurantSelect';
import { NameDishInput } from './components/NameDishInput';
import { PriceInput } from './components/PriceInput';
import { PriceStrategySelect } from './components/PriceStrategySelect';
import { ExternalIdField } from './components/ExternalIdField';
import { IsProtectedFromSyncCheckbox } from './components/IsProtectedFromSyncCheckbox';
import { IsDisabledCheckbox } from './components/IsDisabledCheckbox';
import { MenuTranslationForm } from 'components/TranslationForm/MenuTranslationForm';
import { RestaurantsBlock } from './components/RestaurantsBlock';
import { BtnBlock } from './components/BtnBlock';
import style from './index.module.scss';

interface IProps {
	onCloseModal: () => void;
	onFailureEvent?: () => void;
	onShowError: () => void;
}

export const Forms: React.FC<IProps> = ({ onCloseModal, onFailureEvent, onShowError }) => {
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const {
		changeNameOfPortion,
		changePrice,
		changePriceStrategy,
		changeTypeOfModalWindow,
		changeExternalId,
		generateUniqueExternalId,
		toggleIsProtectedFromSync,
		toggleIsDisabled,
		setTranslationTab,
		changeNameTranslation,
		setAdditionalRestaurants,
		portionId,
		nameOfPortion,
		fetchedCustomers,
		IDSelectedCustomer,
		fetchedRestaurants,
		IDSelectedRestaurant,
		nameOfDish,
		dishId,
		isCreatedOnPos,
		posName,
		articleNumber,
		price,
		priceStrategy,
		externalId,
		isProtectedFromSync,
		isDisabled,
		translations,
		defaultCustomerLanguage,
		selectedTranslationTab,
		selectedAdditionalRestaurants,
	} = useContext<IEditPortionContext>(EditPortionContext);
	const [form] = Form.useForm();
	const [isFirstRender, setIsFirstRender] = useState(true);
	const [step, setStep] = useState<'step1' | 'step2'>('step1');

	useEffect(() => {
		if (isFirstRender && IDSelectedCustomer) {
			setIsFirstRender(false);
		}
	}, [IDSelectedCustomer]);

	useEffect(() => {
		form.setFieldsValue({
			nameOfPortion,
			IDSelectedCustomer,
			IDSelectedRestaurant,
			nameOfDish,
			posName,
			articleNumber,
			price,
			priceStrategy,
			externalId,
			isProtectedFromSync,
			isDisabled,
			selectedAdditionalRestaurants,
		});
	}, [
		nameOfPortion,
		IDSelectedCustomer,
		IDSelectedRestaurant,
		nameOfDish,
		posName,
		articleNumber,
		price,
		priceStrategy,
		externalId,
		isProtectedFromSync,
		isDisabled,
		selectedAdditionalRestaurants,
	]);

	const submit = async () => {
		try {
			const restaurantIds = [IDSelectedRestaurant || '', ...selectedAdditionalRestaurants];
			const convertedData: IUpdatePortion = convertUndefinedFields({
				portionId,
				name: nameOfPortion,
				dishId,
				externalId,
				restaurantIds,
				price,
				priceStrategy,
				isProtectedFromSync,
				isDisabled,
				translations,
			});
			showLoader();
			await updatePortion(convertedData);
			onCloseModal();
		} catch (err) {
			onShowError();
		} finally {
			hideLoader();
		}
	};

	const handleSaveAndApplyOtherLocations = async () => {
		if (step === 'step1') {
			await form.validateFields();
			const defaultTranslationIsEmpty =
				translations.find((translation) => translation.languageId === defaultCustomerLanguage?.id)?.name === '';
			if (defaultTranslationIsEmpty) {
				changeTypeOfModalWindow('defaultTranslationIsEmpty');
				return;
			}
			setStep('step2');
		} else if (step === 'step2') {
			form.submit();
		} else {
			onFailureEvent && onFailureEvent();
		}
	};

	const handleSaveLocalChanges = async () => {
		await form.validateFields();
		const defaultTranslationIsEmpty =
			translations.find((translation) => translation.languageId === defaultCustomerLanguage?.id)?.name === '';
		if (defaultTranslationIsEmpty) {
			changeTypeOfModalWindow('defaultTranslationIsEmpty');
			return;
		}
		form.submit();
	};

	const handleCancel = () => {
		if (step === 'step1') {
			onFailureEvent ? onFailureEvent() : onCloseModal();
		} else if (step === 'step2') {
			setStep('step1');
		} else {
			setStep('step2');
		}
	};

	return (
		<div data-test="Forms">
			<Form form={form} name="basic" onFinish={submit} onKeyPress={preventDefaultForEnterKey} layout="vertical">
				{step === 'step1' && (
					<>
						<Text
							className={style.modalHeader}
							translationNameSpace="form"
							translationKey="editPortionHeading"
							size="xxl"
							weight="bold"
						/>
						<div className={style.fieldsWrapper}>
							<Text
								translationNameSpace="form"
								translationKey="mainInfoHeading"
								theme="secondary"
								size="l"
								weight="bold"
								display="inline"
							/>
							<div className={style.row}>
								<NamePortionInput changeNameOfPortion={changeNameOfPortion} />
								<PosNameInput />
							</div>
							<div className={style.row}>
								<ArticleNumberInput />
								<CustomerSelect fetchedCustomers={fetchedCustomers} />
							</div>
							<div className={style.row}>
								<RestaurantSelect fetchedRestaurants={fetchedRestaurants} />
								<NameDishInput />
							</div>
							<PriceStrategySelect
								changePriceStrategy={changePriceStrategy}
								isDisabled={isCreatedOnPos}
							/>
							<PriceInput changePrice={changePrice} isDisabled={isCreatedOnPos} />
							<div className={style.row}>
								<ExternalIdField changeExternalId={changeExternalId} />
								<Button onClick={generateUniqueExternalId} theme="primary">
									<Text
										translationNameSpace="form"
										translationKey="generateUniqueExternalId"
										asFragment={true}
									/>
								</Button>
							</div>
							<IsProtectedFromSyncCheckbox
								toggleIsProtectedFromSync={toggleIsProtectedFromSync}
								isTaxPercentProtectedFromSync={isProtectedFromSync}
							/>
							<IsDisabledCheckbox toggleIsDisabled={toggleIsDisabled} isDisabled={isDisabled} />
						</div>
						<div className={style.fieldsWrapper}>
							<Text
								translationNameSpace="form"
								translationKey="translationsHeading"
								theme="secondary"
								size="l"
								weight="bold"
							/>
							<MenuTranslationForm
								className={style.translationForm}
								setTranslationTab={setTranslationTab}
								changeNameTranslation={changeNameTranslation}
								isShowLanguageName={false}
								IDSelectedCustomer={IDSelectedCustomer}
								translations={translations}
								selectedTranslationTab={selectedTranslationTab}
								nameLabel="portionNameLabel"
							/>
						</div>
					</>
				)}
				{step === 'step2' && (
					<>
						<Text
							className={style.modalHeader}
							translationNameSpace="form"
							translationKey="applyToOtherLocationsHeading"
							size="xxl"
							weight="bold"
						/>
						<Text
							translationNameSpace="form"
							translationKey="descriptionForFinishPortionEdition"
							size="s"
						/>
						<RestaurantsBlock
							setAdditionalRestaurants={setAdditionalRestaurants}
							selectedAdditionalRestaurants={selectedAdditionalRestaurants}
							fetchedRestaurants={fetchedRestaurants}
							IDSelectedRestaurant={IDSelectedRestaurant}
						/>
					</>
				)}
				<BtnBlock
					onSaveAndApplyOtherLocations={
						step === 'step1' && fetchedRestaurants.length > 1 ? handleSaveAndApplyOtherLocations : undefined
					}
					onSaveLocalChanges={step === 'step1' ? handleSaveLocalChanges : undefined}
					onSave={step === 'step2' ? handleSaveAndApplyOtherLocations : undefined}
					onCancel={handleCancel}
					isDisabledSave={step === 'step2' && !selectedAdditionalRestaurants.length}
				/>
			</Form>
		</div>
	);
};
